import { useState, useRef, useEffect, useCallback } from 'react';
import cn from 'classnames';

import useResize from 'js/hooks/use-resize';
import { convertToPxs, applyPositions, pushBeziers, Point, Bezier } from 'utils/path';

interface Points {
  start?: Point;
  end?: Point;
  curve?: Bezier;
}

interface AnimationState {
  menu: {
    animate: boolean;
    animateIn: boolean;
  };
  hover: {
    animate: boolean;
    animateIn: boolean;
  };
}

interface PathProps {
  start: Point;
  relativeBeziers?: Bezier;
  end: Point;
  className?: string;
  mousePos?: Point;
  hoverAnimationTrigger?: boolean;
  softAnimation?: boolean;
  menuAnimationTrigger?: boolean;
}

const Path: React.FC<PathProps> = ({
  start,
  relativeBeziers = [0, 0, 0, 0],
  end,
  className,
  mousePos,
  hoverAnimationTrigger,
  softAnimation,
  menuAnimationTrigger
}) => {
  const svgRef = useRef<SVGSVGElement>(null);
  const animateRef = useRef<SVGAnimateElement>(null);

  const [points, setPoints] = useState<Points>({});
  const [moveToPoints, setMoveToPoints] = useState<Points | null>(null);
  const [resized, setResized] = useState<number>(0);
  const [animationState, setAnimationState] = useState<AnimationState>({
    menu: { animate: false, animateIn: false },
    hover: { animate: false, animateIn: false }
  });
  const [isAnimatable, setIsAnimatable] = useState(false);
  const [hasMounted, setHasMounted] = useState(false);

  useResize(() => setResized(prev => prev + 1));

  const getPoints = useCallback((): Points => {
    if (!svgRef.current) return {};

    const { clientWidth, clientHeight } = svgRef.current;
    const startPx = convertToPxs(clientWidth, clientHeight, start);
    const endPx = convertToPxs(clientWidth, clientHeight, end);
    const absoluteBeziers = applyPositions(startPx, endPx, relativeBeziers);

    return {
      start: startPx,
      end: endPx,
      curve: absoluteBeziers
    };
  }, [end, relativeBeziers, start]);

  const getLocalMousePos = (mousePos: Point): Point => {
    if (!svgRef.current) return [0, 0];
    const spaceAboveCanvas = svgRef.current.getBoundingClientRect().y;
    return [mousePos[0], mousePos[1] - spaceAboveCanvas];
  };

  const localMousePos = mousePos && svgRef.current ? getLocalMousePos(mousePos) : ([0, 0] as Point);

  const fromPath = (): string => `M${points.start?.toString()} C${points.curve?.toString()} ${points.end?.toString()}`;

  const toPath = (): string => {
    const coords = moveToPoints || points;
    return `M${coords.start?.toString()} C${pushBeziers(coords.curve!, localMousePos, softAnimation).toString()} ${coords.end?.toString()}`;
  };

  useEffect(() => {
    const newPoints = getPoints();
    setPoints(newPoints);
    setIsAnimatable(false);
    setHasMounted(true);
  }, [getPoints, resized]);

  useEffect(() => {
    if (!hasMounted) return;
    setIsAnimatable(true);
    setAnimationState({
      menu: { animate: false, animateIn: !!menuAnimationTrigger },
      hover: { animate: true, animateIn: !!hoverAnimationTrigger }
    });
  }, [hoverAnimationTrigger, hasMounted, menuAnimationTrigger]);

  useEffect(() => {
    if (!hasMounted) return;
    setIsAnimatable(true);
    setAnimationState({
      menu: { animate: true, animateIn: !!menuAnimationTrigger },
      hover: { animate: false, animateIn: !!hoverAnimationTrigger }
    });
  }, [menuAnimationTrigger, hasMounted, hoverAnimationTrigger]);

  useEffect(() => {
    if (!isAnimatable || !animateRef.current) return;

    if (animationState.menu.animate) {
      const newPoints = getPoints();
      setMoveToPoints(newPoints);

      const event = () => {
        setPoints(newPoints);
        setMoveToPoints(null);
        setIsAnimatable(false);
      };
      animateRef.current.addEventListener('endEvent', event);
    }
    animateRef.current.beginElement();
  }, [
    animationState.hover.animateIn,
    animationState.menu.animate,
    animationState.menu.animateIn,
    getPoints,
    isAnimatable
  ]);

  return (
    <svg ref={svgRef} className={cn('path', className)} width="100%" height="100%" fill="none">
      {points.start && (
        <path
          d={fromPath()}
          stroke="currentColor"
          strokeWidth="4"
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeDasharray="4 10"
        >
          {isAnimatable && (
            <animate
              attributeName="d"
              from={animationState.hover.animateIn || moveToPoints ? fromPath() : toPath()}
              to={animationState.hover.animateIn || moveToPoints ? toPath() : fromPath()}
              dur="0.3s"
              begin="indefinite"
              fill="freeze"
              ref={animateRef}
              calcMode="spline"
              keySplines="0.37 0 0.63 1"
              keyTimes="0;1"
            />
          )}
        </path>
      )}
    </svg>
  );
};

Path.displayName = 'Path';

export default Path;
